import { useState } from 'react';
import { X, Save, ClipboardList, DollarSign, Shield, AlertTriangle } from 'lucide-react';

/* ─── Shared slider ─── */
function NativeSlider({ value, min, max, step, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative flex items-center h-10">
      <div className="absolute left-0 right-0 h-[5px] rounded-full"
        style={{ background: 'var(--bg-tertiary, #e5e7eb)' }} />
      <div className="absolute left-0 h-[5px] rounded-full"
        style={{ width: `${pct}%`, background: 'var(--accent-primary, #3b82f6)', transition: 'width .05s' }} />
      <input type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        className="grp-thumb w-full h-10 absolute left-0 cursor-pointer bg-transparent appearance-none" />
    </div>
  );
}

function SliderField({ label, hint, value, displayValue, sliderValue, min, max, step, onChange, onReset }) {
  return (
    <div className="py-3">
      <div className="flex justify-between items-center mb-1">
        <label className="text-[15px] font-medium" style={{ color: 'var(--text-primary, #111)' }}>{label}</label>
        <span className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 12%, transparent)',
            color: 'var(--accent-primary, #3b82f6)',
          }}>
          {displayValue}
        </span>
      </div>
      <div className="space-y-1">
        <NativeSlider value={sliderValue} min={min} max={max} step={step} onChange={onChange} />
        {onReset && (
          <button type="button" onClick={onReset}
            className="text-xs underline underline-offset-2"
            style={{ color: 'var(--accent-primary)' }}>
            Reset to no limit
          </button>
        )}
        {hint && <p className="text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>}
      </div>
    </div>
  );
}

/* ─── Native toggle (mobile) / checkbox (desktop) ─── */
function ToggleRow({ checked, onChange, label }) {
  return (
    <>
      {/* Mobile toggle */}
      <div className="grp-toggle-row flex items-center justify-between py-3">
        <span className="text-[15px] font-medium" style={{ color: 'var(--text-primary,#111)' }}>{label}</span>
        <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
          className="relative flex-shrink-0 w-[51px] h-[31px] rounded-full transition-all duration-200 active:scale-95"
          style={{ background: checked ? 'var(--accent-primary,#3b82f6)' : 'var(--bg-tertiary,#d1d5db)' }}>
          <span className="absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow transition-transform duration-200"
            style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }} />
        </button>
      </div>

      {/* Desktop checkbox */}
      <label className="grp-checkbox-row flex items-center gap-3 cursor-pointer py-2">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          className="w-5 h-5 rounded border-2 border-[var(--border-color)]"
          style={{ accentColor: 'var(--accent-primary)' }} />
        <span className="text-sm font-medium">{label}</span>
      </label>
    </>
  );
}

/* ─── Section form content shared between both layouts ─── */
const SECTIONS = [
  { id: 'basic',   label: 'Basic Info',        icon: '📋' },
  { id: 'pricing', label: 'Pricing Overrides',  icon: '💰' },
  { id: 'risk',    label: 'Risk Management',    icon: '🛡️' },
];

function SectionContent({ activeSection, formData, setFormData }) {
  const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));
  const marginBelowStop = formData.margin_call_level <= formData.stop_out_level;

  if (activeSection === 'basic') return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Basic Information</h3>
      <div>
        <label className="block text-sm font-medium mb-2">Group Name *</label>
        <input type="text" value={formData.name} required
          onChange={e => set('name', e.target.value)}
          placeholder="Standard Group"
          className="w-full px-4 py-2 rounded-lg focus:outline-none"
          style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
          }} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Description</label>
        <textarea value={formData.description} rows={3}
          onChange={e => set('description', e.target.value)}
          placeholder="Group description..."
          className="w-full px-4 py-2 rounded-lg focus:outline-none resize-none"
          style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
          }} />
      </div>
      <div className="space-y-3">
        <ToggleRow checked={formData.is_active} onChange={v => set('is_active', v)} label="Group Active" />
        <ToggleRow
          checked={formData.is_default}
          onChange={v => !formData.is_default && set('is_default', v)}
          label="Default Group" />
      </div>
    </div>
  );

  if (activeSection === 'pricing') return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-1">Pricing Overrides</h3>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Leave blank to use broker-level defaults. Only override if this group needs different pricing.
        </p>
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderField label="Commission Per Lot (USD)"
          hint="USD charged per standard lot traded"
          value={formData.commission_per_lot}
          displayValue={formData.commission_per_lot !== null && formData.commission_per_lot !== '' ? `$${formData.commission_per_lot}` : 'Broker Default'}
          sliderValue={formData.commission_per_lot || 0}
          min={0} max={50} step={0.5}
          onChange={e => set('commission_per_lot', parseFloat(e.target.value))}
          onReset={() => set('commission_per_lot', null)} />
        <SliderField label="Max Leverage"
          hint="Maximum leverage available to this group"
          value={formData.max_leverage}
          displayValue={formData.max_leverage !== null && formData.max_leverage !== '' ? `1:${formData.max_leverage}` : 'Broker Default'}
          sliderValue={formData.max_leverage || 100}
          min={1} max={2000} step={1}
          onChange={e => set('max_leverage', parseInt(e.target.value))}
          onReset={() => set('max_leverage', null)} />
      </div>
    </div>
  );

  if (activeSection === 'risk') return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Risk Management</h3>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderField label="Margin Call Level"
          hint="Alert when margin level reaches this percentage"
          value={formData.margin_call_level}
          displayValue={`${formData.margin_call_level}%`}
          sliderValue={formData.margin_call_level}
          min={20} max={100} step={5}
          onChange={e => set('margin_call_level', parseFloat(e.target.value))} />
        <SliderField label="Stop Out Level"
          hint="Auto-close positions at this margin level"
          value={formData.stop_out_level}
          displayValue={`${formData.stop_out_level}%`}
          sliderValue={formData.stop_out_level}
          min={0} max={50} step={5}
          onChange={e => set('stop_out_level', parseFloat(e.target.value))} />
        <SliderField label="Max Open Positions"
          hint="Maximum number of positions per account"
          value={formData.max_open_positions}
          displayValue={`${formData.max_open_positions}`}
          sliderValue={formData.max_open_positions}
          min={1} max={200} step={5}
          onChange={e => set('max_open_positions', parseInt(e.target.value))} />
        <SliderField label="Max Lot Size"
          hint="Maximum lot size per trade"
          value={formData.max_lot_size}
          displayValue={formData.max_lot_size !== null && formData.max_lot_size !== '' ? `${formData.max_lot_size} lots` : 'No Limit'}
          sliderValue={formData.max_lot_size || 100}
          min={0.01} max={500} step={0.01}
          onChange={e => set('max_lot_size', parseFloat(e.target.value))}
          onReset={() => set('max_lot_size', null)} />
      </div>
      {marginBelowStop && (
        <div className="p-4 rounded-lg"
          style={{
            background: 'color-mix(in srgb, var(--accent-danger,#ef4444) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--accent-danger,#ef4444) 30%, transparent)',
          }}>
          <p className="text-sm flex items-center gap-2" style={{ color: 'var(--accent-danger)' }}>
            <span>⚠️</span> Margin Call Level must be greater than Stop Out Level
          </p>
        </div>
      )}
    </div>
  );

  return null;
}

export default function AccountGroupFormModal({ group, onClose, onSave }) {
  const [activeSection, setActiveSection] = useState('basic');
  const [formData, setFormData] = useState({
    name:               group?.name        || '',
    description:        group?.description || '',
    is_default:         Boolean(group?.is_default),
    is_active:          group?.is_active !== undefined ? Boolean(group.is_active) : true,
    commission_per_lot: group?.commission_per_lot ?? null,
    max_leverage:       group?.max_leverage       ?? null,
    margin_call_level:  group?.margin_call_level  || 50.0,
    stop_out_level:     group?.stop_out_level     || 20.0,
    max_open_positions: group?.max_open_positions || 50,
    max_lot_size:       group?.max_lot_size       ?? null,
  });

  const marginBelowStop = formData.margin_call_level <= formData.stop_out_level;

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (marginBelowStop) return;
    onSave({
      ...formData,
      is_default:         formData.is_default ? 1 : 0,
      is_active:          formData.is_active  ? 1 : 0,
      commission_per_lot: formData.commission_per_lot === '' ? null : formData.commission_per_lot,
      max_leverage:       formData.max_leverage       === '' ? null : formData.max_leverage,
      max_lot_size:       formData.max_lot_size       === '' ? null : formData.max_lot_size,
    });
  };

  return (
    <>
      <style>{`
        .grp-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          box-shadow: 0 1px 4px rgba(0,0,0,.25), 0 0 0 3px color-mix(in srgb, var(--accent-primary,#3b82f6) 18%, transparent);
          border: 2.5px solid #fff; cursor: pointer; transition: transform .1s;
        }
        .grp-thumb:active::-webkit-slider-thumb { transform: scale(1.15); }
        .grp-thumb::-moz-range-thumb {
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          border: none; cursor: pointer;
        }

        /* ── Mobile: bottom sheet + tab bar nav ── */
        @media (max-width: 767px) {
          .grp-backdrop { align-items: flex-end; }
          .grp-dialog {
            width: 100%; max-width: 100%;
            border-radius: 20px 20px 0 0;
            max-height: 92dvh;
            flex-direction: column;
            animation: grpSlideUp .28s cubic-bezier(.32,.72,0,1);
          }
          .grp-handle { display: flex; }
          .grp-sidebar { display: none; }
          .grp-tabbar { display: flex; }
          .grp-footer {
            padding: 12px 16px;
            padding-bottom: max(16px, env(safe-area-inset-bottom));
          }
          .grp-footer button {
            padding-top: 14px; padding-bottom: 14px;
            border-radius: 16px; font-size: 16px; font-weight: 600;
          }
          .grp-toggle-row { display: flex; }
          .grp-checkbox-row { display: none; }
        }

        /* ── Desktop: original sidebar layout ── */
        @media (min-width: 768px) {
          .grp-backdrop { align-items: center; justify-content: center; padding: 16px; }
          .grp-dialog {
            width: 100%; max-width: 896px;
            border-radius: 16px; max-height: 90vh;
            flex-direction: row;
            border: 1px solid var(--border-color);
          }
          .grp-handle { display: none; }
          .grp-sidebar { display: flex; }
          .grp-tabbar { display: none; }
          .grp-footer { padding: 24px; }
          .grp-footer button {
            padding: 12px 24px; border-radius: 8px;
            font-size: 14px; font-weight: 500;
          }
          .grp-toggle-row { display: none; }
          .grp-checkbox-row { display: flex; }
        }

        @keyframes grpSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <div className="grp-backdrop fixed inset-0 z-50 flex"
        style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
        onClick={e => e.target === e.currentTarget && onClose()}>

        <div className="grp-dialog flex overflow-hidden"
          style={{ background: 'var(--bg-primary, #fff)' }}>

          {/* ── Desktop sidebar ── */}
          <div className="grp-sidebar w-56 flex-col flex-shrink-0 overflow-y-auto p-4"
            style={{
              background: 'var(--bg-secondary)',
              borderRight: '1px solid var(--border-color)',
            }}>
            <h3 className="text-sm font-semibold mb-3 px-2">Sections</h3>
            <div className="space-y-1">
              {SECTIONS.map(s => (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{
                    background: activeSection === s.id ? 'var(--accent-primary)' : 'transparent',
                    color: activeSection === s.id ? '#fff' : 'var(--text-secondary)',
                  }}>
                  <span className="mr-2">{s.icon}</span>{s.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Main column (header + tabbar + body + footer) ── */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">

            {/* Mobile drag handle */}
            <div className="grp-handle justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-[5px] rounded-full" style={{ background: 'var(--border-color, #d1d5db)' }} />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
              style={{ borderBottom: '1px solid var(--border-color)' }}>
              <h2 className="text-xl font-bold">
                {group ? 'Edit Account Group' : 'Create Account Group'}
              </h2>
              <button onClick={onClose}
                className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile tab bar */}
            <div className="grp-tabbar gap-1 px-4 py-2 flex-shrink-0"
              style={{ borderBottom: '1px solid var(--border-color)' }}>
              {SECTIONS.map(({ id, label, icon }) => (
                <button key={id} type="button" onClick={() => setActiveSection(id)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  style={{
                    background: activeSection === id ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
                    color: activeSection === id ? '#fff' : 'var(--text-secondary, #6b7280)',
                  }}>
                  <span>{icon}</span> {label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              <SectionContent
                activeSection={activeSection}
                formData={formData}
                setFormData={setFormData}
              />
            </div>

            {/* Footer */}
            <div className="grp-footer flex gap-3 flex-shrink-0"
              style={{ borderTop: '1px solid var(--border-color)' }}>
              <button type="button" onClick={onClose}
                className="flex-1 bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit}
                disabled={marginBelowStop || !formData.name}
                className="flex-1 bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                {group ? 'Update Group' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}